import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { obtenerDashboard } from '../../actions/dashboard';
import { buscarSucursales } from '../../actions/sucursales';
import { buscarEventos } from '../../actions/eventos';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

import FiltrosDashboard from './FiltrosDashboard';

/* ── Catálogos ── */
const tiposEmpleado = ['Cajero', 'Bateador', 'Promotor', 'Freidor', 'Vendedor ambulante', 'Administrador'];

/* ── Helpers ── */
const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' })
    : '-';

const formatNombre = (emp) =>
    `${emp.nombre || ''} ${emp.apellido_paterno || ''} ${emp.apellido_materno || ''}`.trim();

const iniciales = (emp) =>
    `${(emp.nombre || '')[0] || ''}${(emp.apellido_paterno || '')[0] || ''}`.toUpperCase();

/* ── Componente: KPI card grande ── */
const KpiCard = ({ label, total, count, labelCount, color, icon }) => (
    <div className="col-md">
        <div className={`card border-0 shadow-sm h-100 border-start border-${color} border-4`}>
            <div className="card-body d-flex align-items-center gap-3 py-3">
                <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0`}
                    style={{ width: 48, height: 48 }}>
                    <i className={`fas ${icon} text-${color}`}></i>
                </div>
                <div className="overflow-hidden">
                    <div className="text-muted small text-truncate">{label}</div>
                    <div className={`fw-bold fs-5 text-${color}`}>{formatNumber(total ?? 0, 2, '$', '')}</div>
                    {count != null && (
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {count} {labelCount}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
);

/* ── Componente: KPI card compacto (mes) ── */
const KpiMes = ({ label, total, count, labelCount, color }) => (
    <div className="col-md">
        <div className={`border-start border-${color} border-3 rounded-end bg-light px-3 py-2`}>
            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{label}</div>
            <div className={`fw-bold text-${color}`}>{formatNumber(total ?? 0, 2, '$', '')}</div>
            {count != null && (
                <div className="text-muted" style={{ fontSize: '0.68rem' }}>{count} {labelCount}</div>
            )}
        </div>
    </div>
);

/* ── Componente: Gráfica de pastel (donut SVG) ── */
const GraficaPastel = ({ data }) => {
    const { ventas = 0, vueltas = 0, gastos = 0, nominas = 0 } = data;
    const total = ventas + vueltas - gastos - nominas;

    const segmentos = [
        { label: 'Ventas',      value: ventas,  color: '#198754' },
        { label: 'Ventas amb.', value: vueltas, color: '#0dcaf0' },
        { label: 'Gastos',      value: gastos,  color: '#dc3545' },
        { label: 'Nóminas',     value: nominas, color: '#6c757d' },
    ];

    if (total === 0) {
        return <div className="text-center text-muted py-4">Sin datos para mostrar</div>;
    }

    const R = 60;
    const C = 2 * Math.PI * R;
    const SIZE = 170;

    let accumulated = 0;
    const slices = segmentos
        .filter(s => s.value > 0)
        .map(s => {
            const dash   = (s.value / total) * C;
            const offset = C - accumulated;
            accumulated += dash;
            return { ...s, dash, offset, pct: Math.round(s.value / total * 100) };
        });

    return (
        <div className="d-flex align-items-center gap-4 flex-wrap justify-content-center">
            <svg
                width={SIZE}
                height={SIZE}
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', flexShrink: 0 }}
            >
                {slices.map((s, i) => (
                    <circle
                        key={i}
                        cx={SIZE / 2}
                        cy={SIZE / 2}
                        r={R}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={22}
                        strokeDasharray={`${s.dash} ${C - s.dash}`}
                        strokeDashoffset={s.offset}
                    />
                ))}
            </svg>
            <div className="d-flex flex-column gap-3">
                {slices.map(s => (
                    <div key={s.label} className="d-flex align-items-center gap-2">
                        <span style={{ width: 12, height: 12, background: s.color, borderRadius: 2, display: 'inline-block', flexShrink: 0 }}></span>
                        <div>
                            <div className="small fw-semibold">{s.label}</div>
                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                {formatNumber(s.value, 2, '$', '')} &middot; {s.pct}%
                            </div>
                        </div>
                    </div>
                ))}
                <div className="border-top pt-2">
                    <div className="text-muted small">Total</div>
                    <div className="fw-bold">{formatNumber(total, 2, '$', '')}</div>
                </div>
            </div>
        </div>
    );
};

const filtrosDefault = () => {
    const hoy = new Date();
    return {
        sucursal:    null,
        evento:      null,
        fecha_inicio: new Date(new Date(hoy).setHours(0, 0, 0, 0)),
        fecha_fin:    new Date(new Date(hoy).setHours(23, 59, 59, 999))
    };
};

/* ── Vista principal ── */
const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const data = useSelector(state => state.dashboard.data);

    const [filtrosModal, setFiltrosModal] = useState(false);
    const [filtros, setFiltros] = useState(filtrosDefault);

    useEffect(() => {
        dispatch(obtenerDashboard(filtros));
        dispatch(buscarSucursales());
        dispatch(buscarEventos(false));
        dispatch(editarBreadcrumb([
            { nombre: 'Inicio', path: '/dashboard', activo: true }
        ]));
    }, []);

    const handleAplicarFiltros = (nuevosFiltros) => {
        setFiltros(nuevosFiltros);
        dispatch(obtenerDashboard(nuevosFiltros));
    };

    const filtrosActivos = [
        filtros.sucursal,
        filtros.evento,
        filtros.fecha_inicio,
        filtros.fecha_fin
    ].filter(Boolean).length;

    if (!data) {
        return (
            <div className="row mt-5">
                <div className="col-12 text-center py-5">
                    <span className="spinner-border text-primary me-2" role="status" aria-hidden="true"></span>
                    <span className="text-muted">Cargando dashboard...</span>
                </div>
            </div>
        );
    }

    const { finanzas, grafica, revisiones, nomina, total_empleados, top_vendedores, top_alimentos, productos } = data;

    return (
        <div className="row mt-4 g-4">

            {/* ── Header ── */}
            <div className="col-12">
                <div className="d-flex align-items-center justify-content-between">
                    <h4 className="mb-0">Dashboard</h4>
                    <button className="btn btn-secondary position-relative" onClick={() => setFiltrosModal(true)}>
                        <i className="fas fa-filter me-1"></i> Filtros
                        {filtrosActivos > 0 && (
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary">
                                {filtrosActivos}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Sección: Finanzas ── */}
            <div className="col-12">
                <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="fw-semibold text-muted small text-uppercase">Finanzas</span>
                    <hr className="flex-grow-1 my-0" />
                </div>
                <div className="row g-3">
                    <KpiCard label="Ventas"   total={finanzas.ventas.total}   count={finanzas.ventas.count}   labelCount="ventas"  color="success" icon="fa-shopping-bag" />
                    <KpiCard label="Ventas amb."  total={finanzas.vueltas.total}  count={finanzas.vueltas.count}  labelCount="ventas amb." color="success"    icon="fa-redo" />
                    <KpiCard label="Gastos"   total={finanzas.gastos.total}   count={finanzas.gastos.count}   labelCount="gastos"  color="danger"  icon="fa-receipt" />
                    <KpiCard label="Nominas"   total={finanzas.nominas.total}   count={finanzas.nominas.count}   labelCount="nominas"  color="warning"  icon="fa-money-check-alt" />
                    <KpiCard label="Utilidad" total={finanzas.utilidad}                                                       color="info" icon="fa-chart-line" />
                </div>
            </div>

            {/* ── Sección: Productos ── */}
            {(productos || []).length > 0 && (
                <div className="col-12">
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <span className="fw-semibold text-muted small text-uppercase">Productos</span>
                        <hr className="flex-grow-1 my-0" />
                    </div>
                    <div className="row g-3">
                        {(productos || []).map(prod => {
                            const color = prod.tipo === 1 ? 'info' : prod.tipo === 2 ? 'secondary' : 'warning';
                            const icon  = prod.tipo === 1 ? 'fa-glass-whiskey' : prod.tipo === 2 ? 'fa-box-open' : 'fa-ice-cream';
                            return (
                                <div className="col-md" key={prod.nombre}>
                                    <div className={`card border-0 shadow-sm h-100 border-start border-${color} border-4`}>
                                        <div className="card-body d-flex align-items-center gap-3 py-3">
                                            <div className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0`}
                                                style={{ width: 48, height: 48 }}>
                                                <i className={`fas ${icon} text-${color}`}></i>
                                            </div>
                                            <div className="overflow-hidden">
                                                <div className="text-muted small text-truncate">{prod.nombre}</div>
                                                <div className={`fw-bold fs-5 text-${color}`}>{prod.cantidad}</div>
                                                <div className="text-muted" style={{ fontSize: '0.72rem' }}>vendidos</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Sección: Gráfica + Alertas ── */}
            <div className="col-lg-8">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white border-bottom py-3">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-chart-pie text-primary me-2"></i>
                            Distribución
                        </h6>
                    </div>
                    <div className="card-body d-flex align-items-center justify-content-center">
                        <GraficaPastel data={grafica} />
                    </div>
                </div>
            </div>

            <div className="col-lg-4">
                <div className="d-flex flex-column gap-3 h-100">

                    {/* Revisiones */}
                    <div className={`card border-0 shadow-sm border-start border-4 ${revisiones.pendientes > 0 ? 'border-warning' : 'border-success'}`}>
                        <div className="card-body d-flex align-items-center gap-3 py-3">
                            <div className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${revisiones.pendientes > 0 ? 'bg-warning bg-opacity-10' : 'bg-success bg-opacity-10'}`}
                                style={{ width: 44, height: 44 }}>
                                <i className={`fas fa-clipboard-check ${revisiones.pendientes > 0 ? 'text-warning' : 'text-success'}`}></i>
                            </div>
                            <div>
                                <div className="text-muted small">Revisiones</div>
                                {revisiones.pendientes > 0 ? (
                                    <>
                                        <div className="fw-bold text-warning">{revisiones.pendientes} pendiente{revisiones.pendientes !== 1 ? 's' : ''}</div>
                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>{revisiones.total} en total</div>
                                    </>
                                ) : (
                                    <div className="fw-bold text-success">Al corriente</div>
                                )}
                            </div>
                            {revisiones.pendientes > 0 && (
                                <button className="btn btn-sm btn-warning ms-auto" onClick={() => navigate('/revisiones')}>
                                    Ver
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Nómina */}
                    <div className={`card border-0 shadow-sm border-start border-4 ${nomina.activa ? 'border-info' : 'border-secondary'}`}>
                        <div className="card-body d-flex align-items-center gap-3 py-3">
                            <div className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${nomina.activa ? 'bg-info bg-opacity-10' : 'bg-secondary bg-opacity-10'}`}
                                style={{ width: 44, height: 44 }}>
                                <i className={`fas fa-file-invoice-dollar ${nomina.activa ? 'text-info' : 'text-secondary'}`}></i>
                            </div>
                            <div>
                                <div className="text-muted small">Nómina</div>
                                {nomina.activa ? (
                                    <>
                                        <div className="fw-bold text-info">En proceso</div>
                                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                                            Desde {formatFecha(nomina.fecha_inicio)}
                                        </div>
                                    </>
                                ) : (
                                    <div className="fw-bold text-secondary">Sin nómina activa</div>
                                )}
                            </div>
                            <button className="btn btn-sm btn-outline-secondary ms-auto" onClick={() => navigate('/nominas')}>
                                Ver
                            </button>
                        </div>
                    </div>

                    {/* Total empleados */}
                    <div className="card border-0 shadow-sm border-start border-4 border-secondary">
                        <div className="card-body d-flex align-items-center gap-3 py-3">
                            <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 44, height: 44 }}>
                                <i className="fas fa-users text-secondary"></i>
                            </div>
                            <div>
                                <div className="text-muted small">Empleados activos</div>
                                <div className="fw-bold fs-5">{total_empleados}</div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* ── Sección: Top alimentos ── */}
            <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white border-bottom py-3">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-ice-cream text-info me-2"></i>
                            Top productos (totales)
                        </h6>
                    </div>
                    <div className="card-body p-0">
                        {(top_alimentos || []).length === 0 ? (
                            <p className="text-muted text-center py-4 mb-0">Sin datos</p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {(top_alimentos || []).map((alimento, i) => (
                                    <li key={alimento._id} className="list-group-item d-flex align-items-center gap-3 py-3 px-3">
                                        {/* Posición */}
                                        <div className="fw-bold text-muted flex-shrink-0" style={{ width: 20, textAlign: 'center', fontSize: '0.8rem' }}>
                                            {i === 0
                                                ? <i className="fas fa-trophy text-warning"></i>
                                                : i === 1
                                                ? <i className="fas fa-trophy text-secondary"></i>
                                                : i === 2
                                                ? <i className="fas fa-trophy" style={{ color: '#cd7f32' }}></i>
                                                : <span>{i + 1}</span>
                                            }
                                        </div>
                                        {/* Ícono */}
                                        <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{ width: 38, height: 38 }}>
                                            <i className="fas fa-ice-cream text-info small"></i>
                                        </div>
                                        {/* Nombre */}
                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="fw-semibold text-truncate">{alimento.nombre}</div>
                                        </div>
                                        {/* Conos */}
                                        <div className="text-end flex-shrink-0">
                                            <div className="fw-bold text-info small">{alimento.conos}</div>
                                            <div className="text-muted" style={{ fontSize: '0.68rem' }}>vendidos</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Sección: Top vendedores ── */}
            <div className="col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-white border-bottom py-3">
                        <h6 className="mb-0 fw-semibold">
                            <i className="fas fa-trophy text-warning me-2"></i>
                            Top vendedores  (totales)
                        </h6>
                    </div>
                    <div className="card-body p-0">
                        {top_vendedores.length === 0 ? (
                            <p className="text-muted text-center py-4 mb-0">Sin datos</p>
                        ) : (
                            <ul className="list-group list-group-flush">
                                {top_vendedores.map((vend, i) => (
                                    <li key={vend._id} className="list-group-item d-flex align-items-center gap-3 py-3 px-3">
                                        {/* Posición */}
                                        <div className="fw-bold text-muted flex-shrink-0" style={{ width: 20, textAlign: 'center', fontSize: '0.8rem' }}>
                                            {i === 0
                                                ? <i className="fas fa-trophy text-warning"></i>
                                                : i === 1
                                                ? <i className="fas fa-trophy text-secondary"></i>
                                                : i === 2
                                                ? <i className="fas fa-trophy" style={{ color: '#cd7f32' }}></i>
                                                : <span>{i + 1}</span>
                                            }
                                        </div>
                                        {/* Avatar */}
                                        <div className="rounded-circle bg-info bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0 fw-bold text-primary"
                                            style={{ width: 38, height: 38, fontSize: '0.75rem' }}>
                                            {iniciales(vend)}
                                        </div>
                                        {/* Info */}
                                        <div className="flex-grow-1 overflow-hidden">
                                            <div className="fw-semibold text-truncate">{formatNombre(vend)}</div>
                                            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                                                {tiposEmpleado[vend.tipo] || '-'}
                                            </div>
                                        </div>
                                        {/* Stats */}
                                        <div className="text-end flex-shrink-0">
                                            <div className="fw-bold text-success small">{formatNumber(vend.conos, 0, '', '')}</div>
                                            <div className="text-muted" style={{ fontSize: '0.68rem' }}>conos</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            <FiltrosDashboard
                show={filtrosModal}
                handleCerrar={() => setFiltrosModal(false)}
                filtros={filtros}
                handleAplicar={handleAplicarFiltros}
            />

        </div>
    );
};

export default Dashboard;
